import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileAlt, FaArrowLeft, FaSpinner, FaDownload, FaPrint, FaUsers, FaUserTie, FaChartLine } from 'react-icons/fa';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency helper
const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  const numValue = Math.round(value);
  if (numValue >= 10000000) return `₹${(numValue / 10000000).toFixed(2)}Cr`;
  if (numValue >= 100000) return `₹${(numValue / 100000).toFixed(2)}L`;
  return `₹${numValue.toLocaleString('en-IN')}`;
};

const TeamReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, advisorsRes, clientsRes, brandingRes] = await Promise.all([
        axios.get(`${API}/manager/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/manager/advisors`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/manager/client-gaps`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/manager-branding`, { headers }).catch(() => ({ data: null }))
      ]);

      setReportData({
        stats: statsRes.data || {},
        advisors: advisorsRes.data || [],
        clientGaps: clientsRes.data || [],
        branding: brandingRes.data,
        generatedAt: new Date().toISOString()
      });
    } catch (error) {
      // Always render something, never leave reportData null
      setReportData({
        stats: {},
        advisors: [],
        clientGaps: [],
        branding: null,
        generatedAt: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setGenerating(true);
    try {
      const reportElement = reportRef.current;
      if (!reportElement) return;

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0F172A',
        logging: false
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      
      let heightLeft = scaledHeight;
      let position = 0;
      let pageNumber = 1;

      while (heightLeft > 0) {
        if (pageNumber > 1) {
          pdf.addPage();
        }
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, scaledHeight);
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      pdf.save(`Team_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      alert('PDF generation failed. Please try Print instead.');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <FaSpinner className="animate-spin text-[#FFD700]" size={48} />
      </div>
    );
  }

  const { stats = {}, advisors = [], clientGaps = [], branding } = reportData || {};

  // Prepare chart data
  const advisorPerformanceData = advisors.map(adv => ({
    name: adv.name?.split(' ')[0] || 'Advisor',
    clients: adv.client_count || 0,
    plans: adv.completed_plans || 0
  }));

  const gapDistributionData = [
    { name: 'Life Gap', value: clientGaps.filter(c => c.life_gap > 0).length, fill: '#EF4444' },
    { name: 'Health Gap', value: clientGaps.filter(c => c.health_gap > 0).length, fill: '#F97316' },
    { name: 'Emergency Gap', value: clientGaps.filter(c => c.emergency_gap > 0).length, fill: '#FBBF24' },
    { name: 'No Gaps', value: clientGaps.filter(c => !c.life_gap && !c.health_gap && !c.emergency_gap).length, fill: '#22C55E' }
  ];

  const totalLifeGap = clientGaps.reduce((sum, c) => sum + (c.life_gap || 0), 0);
  const totalHealthGap = clientGaps.reduce((sum, c) => sum + (c.health_gap || 0), 0);
  const totalEmergencyGap = clientGaps.reduce((sum, c) => sum + (c.emergency_gap || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <FaArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Team Performance Report</h1>
            <p className="text-gray-400">Comprehensive overview of your team's performance</p>
          </div>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F] flex items-center space-x-2"
          >
            <FaPrint />
            <span>Print Report</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={generating}
            className="px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-2"
            data-testid="download-team-report-btn"
          >
            {generating ? <FaSpinner className="animate-spin" /> : <FaDownload />}
            <span>{generating ? 'Generating...' : 'Download PDF'}</span>
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div ref={reportRef} className="bg-[#0F172A] p-6 rounded-xl print:p-0 print:bg-white">
        {/* Report Header */}
        <div className="bg-gradient-to-r from-[#1A2942] via-[#1E3A5F] to-[#1A2942] p-6 rounded-xl mb-6 print:rounded-none">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-[#FFD700] mb-2">TEAM PERFORMANCE REPORT</h1>
            <p className="text-gray-300">{branding?.company_name || 'Arth Gurukul'}</p>
            <p className="text-gray-400 text-sm mt-2">
              Generated on: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <div className="flex items-center space-x-3">
              <FaUserTie className="text-blue-400 text-2xl" />
              <div>
                <p className="text-gray-400 text-sm">Total Advisors</p>
                <p className="text-white font-bold text-2xl">{stats?.total_advisors || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <div className="flex items-center space-x-3">
              <FaUsers className="text-[#FFD700] text-2xl" />
              <div>
                <p className="text-gray-400 text-sm">Total Clients</p>
                <p className="text-white font-bold text-2xl">{stats?.total_clients || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <div className="flex items-center space-x-3">
              <FaFileAlt className="text-green-400 text-2xl" />
              <div>
                <p className="text-gray-400 text-sm">Completed Plans</p>
                <p className="text-white font-bold text-2xl">{stats?.completed_plans || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <div className="flex items-center space-x-3">
              <FaChartLine className="text-red-400 text-2xl" />
              <div>
                <p className="text-gray-400 text-sm">Clients with Gaps</p>
                <p className="text-white font-bold text-2xl">
                  {clientGaps.filter(c => c.life_gap > 0 || c.health_gap > 0 || c.emergency_gap > 0).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <h3 className="text-white font-bold mb-4">Advisor Performance</h3>
            <div style={{ width: '100%', height: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={advisorPerformanceData}>
                  <XAxis dataKey="name" tick={{ fill: '#9CA3AF' }} />
                  <YAxis tick={{ fill: '#9CA3AF' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F' }} />
                  <Legend />
                  <Bar dataKey="clients" name="Clients" fill="#FFD700" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="plans" name="Plans" fill="#22C55E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
            <h3 className="text-white font-bold mb-4">Client Gap Distribution</h3>
            <div style={{ width: '100%', height: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={gapDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {gapDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Gap Summary */}
        <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F] mb-6">
          <h3 className="text-white font-bold mb-4">Total Gap Analysis</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[#0F172A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Life Insurance Gap</p>
              <p className="text-red-400 font-bold text-2xl">{formatCurrency(totalLifeGap)}</p>
            </div>
            <div className="bg-[#0F172A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Health Insurance Gap</p>
              <p className="text-orange-400 font-bold text-2xl">{formatCurrency(totalHealthGap)}</p>
            </div>
            <div className="bg-[#0F172A] rounded-lg p-4">
              <p className="text-gray-400 text-sm">Total Emergency Fund Gap</p>
              <p className="text-yellow-400 font-bold text-2xl">{formatCurrency(totalEmergencyGap)}</p>
            </div>
          </div>
        </div>

        {/* Advisor Details Table */}
        <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F] mb-6">
          <h3 className="text-white font-bold mb-4">Advisor Performance Details</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-[#2D5A8F]">
                <th className="px-4 py-3 text-left">Advisor Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-center">Clients</th>
                <th className="px-4 py-3 text-center">Completed Plans</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {advisors.map((advisor, idx) => (
                <tr key={idx} className="border-b border-[#2D5A8F]/50">
                  <td className="px-4 py-3 text-white font-medium">{advisor.name}</td>
                  <td className="px-4 py-3 text-gray-300">{advisor.email}</td>
                  <td className="px-4 py-3 text-center text-white font-bold">{advisor.client_count || 0}</td>
                  <td className="px-4 py-3 text-center text-[#FFD700] font-bold">{advisor.completed_plans || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      advisor.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {advisor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Client Gap Details Table */}
        <div className="bg-[#1A2942] rounded-lg p-4 border border-[#2D5A8F]">
          <h3 className="text-white font-bold mb-4">Client Mapping &amp; Gap Details</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-[#2D5A8F]">
                <th className="px-4 py-3 text-left">Client Name</th>
                <th className="px-4 py-3 text-center">Plan</th>
                <th className="px-4 py-3 text-left">Advisor</th>
                <th className="px-4 py-3 text-left">Manager</th>
                <th className="px-4 py-3 text-right">Life Gap</th>
                <th className="px-4 py-3 text-right">Health Gap</th>
                <th className="px-4 py-3 text-right">Emergency Gap</th>
              </tr>
            </thead>
            <tbody>
              {clientGaps.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400">No clients found.</td>
                </tr>
              ) : clientGaps.map((client, idx) => (
                <tr key={idx} className="border-b border-[#2D5A8F]/50" data-testid={`client-gap-row-${idx}`}>
                  <td className="px-4 py-3 text-white font-medium">{client.name || client.client_name || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                      client.plan === 'pro_plus'
                        ? 'bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40'
                        : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                    }`}>
                      {client.plan === 'pro_plus' ? 'PRO+ (Paid)' : 'Plus (Free)'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{client.advisor_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-300">{client.manager_name || '—'}</td>
                  <td className={`px-4 py-3 text-right font-bold ${client.life_gap > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {client.life_gap > 0 ? formatCurrency(client.life_gap) : '✓ Covered'}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${client.health_gap > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                    {client.health_gap > 0 ? formatCurrency(client.health_gap) : '✓ Covered'}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${client.emergency_gap > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                    {client.emergency_gap > 0 ? formatCurrency(client.emergency_gap) : '✓ Covered'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Report Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            This report is generated automatically by Arth Gurukul. 
            For any queries, please contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamReport;
